import { useEffect, useRef, useState } from "react";
import { getUsers } from "../../services/user.service";
import { getAllAccess, grantAccess, revokeAccess } from "../../services/access.service";
import { getAnalyticsSummary } from "../../services/analytics.service.js";
import { formatDate } from "../../utils/date";
import LoadTrigger from "../../components/common/LoadTrigger";
import PageHeader from "../../components/common/PageHeader";
import "../../styles/studio/adminDashboard.css";

const TABS = ["Users", "Access"];
const formatMetric = (value) => new Intl.NumberFormat().format(value ?? 0);

// ── Users Tab ─────────────────────────────────────────────────────────────────
const UsersTab = () => {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const pageRef = useRef(1);
  const reqIdRef = useRef(1);
  const syncRef = useRef(false);

  const onLoadMore = async (reset = false) => {
    if (!reset && (syncRef.current || !hasMore)) return;
    setIsLoading(true);
    syncRef.current = true;
    try {
      const query = { page: reset ? 1 : pageRef.current, ...(search ? { search } : {}) };
      if (reset) { pageRef.current = 2; ++reqIdRef.current; setHasMore(true); }
      else pageRef.current++;
      const reqId = reqIdRef.current;
      const data = await getUsers(query);
      if (reqId !== reqIdRef.current) return;
      setHasMore(data.page < data.totalPages);
      setRecords((prev) => reset ? data.records : [...prev, ...data.records]);
    } catch { --pageRef.current; }
    finally { setIsLoading(false); syncRef.current = false; }
  };

  useEffect(() => {
    const t = setTimeout(() => onLoadMore(true), 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="admin-tab">
      <input
        className="admin-search"
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Publications</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {records.map((u) => (
              <tr key={u.userId || u._id}>
                <td>{u.firstName} {u.lastName}</td>
                <td>{u.email}</td>
                <td><span className={`admin-role ${u.role}`}>{u.role}</span></td>
                <td>{u.publicationsCount ?? 0}</td>
                <td>{formatDate(u.createdAt)}</td>
              </tr>
            ))}
            {!isLoading && records.length === 0 && (
              <tr><td colSpan={5} className="admin-empty">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <LoadTrigger hasMore={hasMore} isLoading={isLoading} onLoadMore={onLoadMore} />
    </div>
  );
};

// ── Access Tab ────────────────────────────────────────────────────────────────
const AccessTab = () => {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [grantForm, setGrantForm] = useState({ userId: "", contentType: "publication", contentId: "", pricingModel: "lifetime", expiresAt: "" });
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState("");
  const [grantSuccess, setGrantSuccess] = useState("");
  const pageRef = useRef(1);
  const reqIdRef = useRef(1);
  const syncRef = useRef(false);

  const onLoadMore = async (reset = false) => {
    if (!reset && (syncRef.current || !hasMore)) return;
    setIsLoading(true);
    syncRef.current = true;
    try {
      const query = { page: reset ? 1 : pageRef.current, ...(filterType !== "all" ? { contentType: filterType } : {}) };
      if (reset) { pageRef.current = 2; ++reqIdRef.current; setHasMore(true); }
      else pageRef.current++;
      const reqId = reqIdRef.current;
      const data = await getAllAccess(query);
      if (reqId !== reqIdRef.current) return;
      setHasMore(data.page < data.totalPages);
      setRecords((prev) => reset ? data.records : [...prev, ...data.records]);
    } catch { --pageRef.current; }
    finally { setIsLoading(false); syncRef.current = false; }
  };

  useEffect(() => {
    const t = setTimeout(() => onLoadMore(true), 100);
    return () => clearTimeout(t);
  }, [filterType]);

  const handleGrant = async (e) => {
    e.preventDefault();
    setGrantError(""); setGrantSuccess("");
    setGrantLoading(true);
    try {
      await grantAccess({
        ...grantForm,
        expiresAt: grantForm.expiresAt || undefined,
      });
      setGrantSuccess("Access granted.");
      onLoadMore(true);
    } catch (err) {
      setGrantError(err.message || "Failed to grant access");
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevoke = async (userId, contentType, contentId) => {
    if (!confirm("Revoke this access?")) return;
    try {
      await revokeAccess({ userId, contentType, contentId });
      setRecords((prev) => prev.filter(
        (r) => !(r.userId?._id === userId && r.contentType === contentType && r.contentId === contentId)
      ));
    } catch (err) {
      alert(err.message || "Failed to revoke");
    }
  };

  return (
    <div className="admin-tab">
      {/* Grant form */}
      <div className="admin-grant-form">
        <h3 className="admin-section-title">Grant Access</h3>
        <form onSubmit={handleGrant} className="admin-form">
          <input className="admin-input" placeholder="User ID" value={grantForm.userId} onChange={(e) => setGrantForm((p) => ({ ...p, userId: e.target.value }))} required />
          <select className="admin-input" value={grantForm.contentType} onChange={(e) => setGrantForm((p) => ({ ...p, contentType: e.target.value }))}>
            <option value="publication">Publication</option>
            <option value="animation">Animation</option>
            <option value="folder">Folder</option>
          </select>
          <input className="admin-input" placeholder="Content ID" value={grantForm.contentId} onChange={(e) => setGrantForm((p) => ({ ...p, contentId: e.target.value }))} required />
          <select className="admin-input" value={grantForm.pricingModel} onChange={(e) => setGrantForm((p) => ({ ...p, pricingModel: e.target.value }))}>
            <option value="lifetime">Lifetime</option>
            <option value="subscription">Subscription</option>
          </select>
          {grantForm.pricingModel === "subscription" && (
            <input className="admin-input" type="datetime-local" value={grantForm.expiresAt} onChange={(e) => setGrantForm((p) => ({ ...p, expiresAt: e.target.value }))} required />
          )}
          {grantError && <p className="admin-error">{grantError}</p>}
          {grantSuccess && <p className="admin-success">{grantSuccess}</p>}
          <button className="admin-btn primary" type="submit" disabled={grantLoading}>
            {grantLoading ? "Granting..." : "Grant Access"}
          </button>
        </form>
      </div>

      {/* Access records */}
      <h3 className="admin-section-title">Active Access Records</h3>
      <div className="admin-filters">
        {["all", "publication", "animation", "folder"].map((f) => (
          <button key={f} className={`admin-filter-btn ${filterType === f ? "active" : ""}`} onClick={() => setFilterType(f)}>
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Type</th>
              <th>Content ID</th>
              <th>Model</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={i}>
                <td>{r.userId?.email || r.userId}</td>
                <td>{r.contentType}</td>
                <td className="admin-mono">{String(r.contentId).slice(-8)}</td>
                <td><span className={`admin-role ${r.pricingModel}`}>{r.pricingModel}</span></td>
                <td>{r.expiresAt ? formatDate(r.expiresAt) : "Lifetime"}</td>
                <td>
                  <button className="admin-revoke-btn" onClick={() => handleRevoke(r.userId?._id || r.userId, r.contentType, r.contentId)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && records.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">No access records.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <LoadTrigger hasMore={hasMore} isLoading={isLoading} onLoadMore={onLoadMore} />
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [tab, setTab] = useState("Users");
  const [analyticsSummary, setAnalyticsSummary] = useState(null);

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      try {
        const summary = await getAnalyticsSummary();
        if (active) {
          setAnalyticsSummary(summary);
        }
      } catch {
        if (active) {
          setAnalyticsSummary(null);
        }
      }
    };

    loadSummary();

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <PageHeader
        title="Admin Dashboard"
        description="Manage users, content access, and site-wide viewer analytics."
      />
      {analyticsSummary && (
        <div className="admin-metrics">
          <div className="admin-metric-card">
            <span>Unique viewers</span>
            <strong>{formatMetric(analyticsSummary.totalUniqueViewers)}</strong>
          </div>
          <div className="admin-metric-card">
            <span>Logged-in viewers</span>
            <strong>{formatMetric(analyticsSummary.loggedInViewers)}</strong>
          </div>
          <div className="admin-metric-card">
            <span>Anonymous viewers</span>
            <strong>{formatMetric(analyticsSummary.anonymousViewers)}</strong>
          </div>
        </div>
      )}
      <div className="admin-tabs">
        {TABS.map((t) => (
          <button key={t} className={`admin-tab-btn ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Users" && <UsersTab />}
      {tab === "Access" && <AccessTab />}
    </>
  );
};

export default AdminDashboard;
